import { effect, Injectable, signal } from '@angular/core';
import { storage } from './storage';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'cortito-theme';
const MODES: ThemeMode[] = ['light', 'dark', 'system'];

/** daisyUI picks the palette from `data-theme`; dropping it falls back to the OS. */
const DAISY_THEME: Record<Exclude<ThemeMode, 'system'>, string> = {
  light: 'brutal',
  dark: 'brutal-dark',
};

/**
 * The stored choice is applied twice: by the inline script in `index.html`
 * before the first paint, and here once Angular boots. Both must agree —
 * `STORAGE_KEY` and the palette names are repeated there on purpose, since
 * that script runs long before this bundle exists.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly mode = signal<ThemeMode>(readStoredMode());

  constructor() {
    effect(() => {
      const mode = this.mode();
      const root = document.documentElement;
      if (mode === 'system') {
        root.removeAttribute('data-theme');
        // Inline, so it beats the `color-scheme` the daisyUI theme sets.
        root.style.colorScheme = 'light dark';
      } else {
        root.dataset['theme'] = DAISY_THEME[mode];
        root.style.colorScheme = mode;
      }
      storage()?.setItem(STORAGE_KEY, mode);
    });
  }

  next(): void {
    this.mode.update((mode) => MODES[(MODES.indexOf(mode) + 1) % MODES.length]);
  }
}

function readStoredMode(): ThemeMode {
  const stored = storage()?.getItem(STORAGE_KEY);
  return MODES.includes(stored as ThemeMode) ? (stored as ThemeMode) : 'light';
}
